import { EVERY_DAY, ISO_WEEKDAYS, isoWeekday, MAX_COATS, MAX_HOURS, MaterialsBy, MIN_COATS, MIN_HOURS, TermKey, Terms, TimeWindow, TRADE_TERMS, } from "@gigsync/protocol";
import { whenPassed } from "../schedule/windows.js";
/**
 * Negotiating the terms other than price (ADR 0033 §3), as plain data: each side has a stance (its own first choice
 * per term, and which values it accepts), and answers the other side's proposal term by term. A term it accepts is
 * kept as proposed; for one it does not, code lists the valid counter-proposals, closest to the other side's value
 * first, and a model may choose among them (`terms-model.ts`), never outside them. Nothing here sets a price.
 */
const K = TermKey.enum;
/** How many days after the asked day a counter-proposal may move the work. */
export const DAYS_AHEAD = 3;
/** At most this many counter-proposals per term are offered to the model to choose from. */
export const MAX_OPTIONS = 3;
const MS_PER_DAY = 86_400_000;
const ISO_DAY_LENGTH = 10;
const WINDOWS = TimeWindow.options;
const addDays = (iso, days) => new Date(Date.parse(iso) + days * MS_PER_DAY).toISOString().slice(0, ISO_DAY_LENGTH);
const dayGap = (a, b) => a === EVERY_DAY || b === EVERY_DAY ? 0 : Math.round((Date.parse(a) - Date.parse(b)) / MS_PER_DAY);
const windowGap = (a, b) => Math.abs(WINDOWS.indexOf(a) - WINDOWS.indexOf(b));
const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);
/** Two values of one term are the same proposal. */
export function sameTerm(key, a, b) {
    if (a === undefined || b === undefined)
        return a === b;
    if (key !== K.when)
        return a === b;
    const [x, y] = [a, b];
    return x.day === y.day && x.window === y.window;
}
/** Every value a term could take near `around`, with how far each is from it; the day only moves later. */
function candidates(key, around) {
    const all = (values, gap) => values.map((value) => ({ value: value, gap: gap(value) }));
    switch (key) {
        case K.when: {
            const w = around;
            const days = w.day === EVERY_DAY ? [EVERY_DAY] : range(0, DAYS_AHEAD).map((d) => addDays(w.day, d));
            const whens = days.flatMap((day) => WINDOWS.map((window) => ({ day, window })));
            return all(whens, (v) => dayGap(v.day, w.day) * WINDOWS.length + windowGap(v.window, w.window));
        }
        case K.materials:
            return all(MaterialsBy.options, (v) => (v === around ? 0 : 1));
        case K.coats:
            return all(range(MIN_COATS, MAX_COATS), (v) => Math.abs(v - around));
        default:
            return all(range(MIN_HOURS, MAX_HOURS), (v) => Math.abs(v - around));
    }
}
/** The values this side accepts other than `theirs`, closest to it first, at most `MAX_OPTIONS`. */
export function counterOptions(key, theirs, stance) {
    const accepts = stance.accepts[key];
    return candidates(key, theirs)
        .filter((c) => !sameTerm(key, c.value, theirs) && accepts(c.value))
        .sort((a, b) => a.gap - b.gap)
        .slice(0, MAX_OPTIONS)
        .map((c) => c.value);
}
/** Whether this side accepts `value` for `key`. */
const accepted = (stance, key, value) => stance.accepts[key](value);
/**
 * This side's answer to the other side's proposal, for each term the trade negotiates: kept when acceptable, else
 * countered with the closest acceptable value (or this side's own choice, when nothing near is acceptable; `stuck`
 * when it has none). A term the other side left out is proposed only when this side wants one.
 */
export function answerTerms(trade, stance, theirs) {
    const next = {};
    const options = {};
    const stuck = [];
    let agreed = true;
    for (const key of TRADE_TERMS[trade]) {
        const proposed = theirs?.[key];
        const want = stance.wants[key];
        if (proposed === undefined) {
            if (want !== undefined)
                [next[key], agreed] = [want, false];
            continue;
        }
        if (accepted(stance, key, proposed)) {
            next[key] = proposed;
            continue;
        }
        agreed = false;
        const counters = counterOptions(key, proposed, stance);
        if (counters.length > 0)
            options[key] = counters;
        else if (want === undefined)
            stuck.push(key);
        next[key] = counters[0] ?? want ?? proposed;
    }
    return { next: Terms.parse(next), options: options, agreed, stuck };
}
/** A value one step from another: the same day or the next, one coat or one hour either way. */
function near(key, a, b) {
    if (key === K.when)
        return Math.abs(dayGap(a.day, b.day)) <= 1;
    if (key === K.materials)
        return true;
    return Math.abs(a - b) <= 1;
}
/**
 * The household's stance, from its owner's own words and chips. A term the owner did not mention, anything goes;
 * one they did, only their value, or a value near it when they said they are flexible about it. A value the worker's
 * agent proposes a second time running is taken too: it is firm, and the owner decides when signing, never the agent
 * (`mustReview`).
 */
export function householdStance(wants, flexible, workerBefore) {
    const accepts = (key) => ((value) => {
        const want = wants[key];
        if (want === undefined || sameTerm(key, value, want))
            return true;
        if (flexible.includes(key) && near(key, value, want))
            return true;
        return sameTerm(key, value, workerBefore?.[key]);
    });
    return {
        wants,
        accepts: {
            when: accepts(K.when),
            materials: accepts(K.materials),
            coats: accepts(K.coats),
            hours: accepts(K.hours),
        },
    };
}
/**
 * The worker's stance, from their card's preferences and the work they have already agreed: a window they take work
 * in that is not booked, the materials they asked for, no more than their hours. Any number of coats: the price
 * follows them through the scope (`termDetails`). With a day to work on and preferred windows, they propose the first
 * free window when the household named none. With `now`, a time already over is never free (ADR 0053 §2).
 */
export function workerStance(prefs, booked, day, now) {
    const windows = prefs?.windows?.length ? prefs.windows : WINDOWS;
    // A day the worker does not work (ADR 0050 §2) is never free; work every day is judged by its window alone.
    const workDay = (d) => !prefs?.days?.length || d === EVERY_DAY || prefs.days.includes(isoWeekday(d));
    const free = (w) => workDay(w.day) && windows.includes(w.window) && !booked.some((b) => clashes(b, w)) && !(now && whenPassed(w, now));
    const first = day && prefs?.windows?.length ? windows.map((window) => ({ day, window })).find(free) : undefined;
    const maxHours = prefs?.max_hours ?? MAX_HOURS;
    return {
        wants: {
            ...(first ? { when: first } : {}),
            ...(prefs?.materials ? { materials: prefs.materials } : {}),
        },
        accepts: {
            when: free,
            materials: (m) => prefs?.materials === undefined || m === prefs.materials,
            coats: () => true,
            hours: (h) => h <= maxHours,
        },
    };
}
/** Two times overlap: the same window on the same day, or on any day for work done every day. */
export const clashes = (a, b) => a.window === b.window && (a.day === b.day || a.day === EVERY_DAY || b.day === EVERY_DAY);
/** The terms the owner asked for that the agreed terms change: the owner signs those themselves. */
export function changedFromWants(wants, agreed) {
    return TermKey.options.filter((key) => wants[key] !== undefined && !sameTerm(key, agreed?.[key], wants[key]));
}
/**
 * The working hours a card shows (ADR 0050 §2), from the worker's preferences: the days and parts of the day they
 * take work, each every one when only the other is set; none when they set neither, which works any time.
 */
export function workHoursOf(prefs) {
    if (!prefs?.days?.length && !prefs?.windows?.length)
        return undefined;
    return {
        days: prefs.days?.length ? [...prefs.days] : [...ISO_WEEKDAYS],
        windows: prefs.windows?.length ? [...prefs.windows] : [...TimeWindow.options],
    };
}
//# sourceMappingURL=terms.js.map